import { useState, useEffect } from "react";
import { Icon } from "@/components/Icon";
import { iconNames } from "@/components/icons";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import type { NMPhoto } from "@/components/kii/kii-editor";

// Simple API request helper (shared with kii-editor.tsx)
async function apiRequest(method: string, url: string, data?: unknown): Promise<Response> {
  const res = await fetch(url, {
    method,
    headers: data ? { "Content-Type": "application/json" } : {},
    body: data ? JSON.stringify(data) : undefined,
    credentials: "include",
  });
  if (!res.ok) {
    throw new Error(`${res.status}: ${res.statusText}`);
  }
  return res;
}

export interface KiiImageThumbnailProps {
  photo: NMPhoto;
  onClick?: () => void;
  selected?: boolean;
  className?: string;
  actions?: React.ReactNode;
  /** Preserve original aspect ratio instead of forcing square crop */
  preserveAspectRatio?: boolean;
}

export function KiiImageThumbnail({
  photo,
  onClick,
  selected,
  className,
  actions,
  preserveAspectRatio,
}: KiiImageThumbnailProps) {
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);

  // Fetch signed URL on mount
  useEffect(() => {
    const gcsUrl = photo.url || photo.filename;
    if (!gcsUrl) {
      setIsLoading(false);
      setError(true);
      return;
    }

    // If it's already a pre-signed URL (starts with http), use directly
    if (photo.url && photo.url.startsWith("http")) {
      setSignedUrl(photo.url);
      setIsLoading(false);
      return;
    }

    const fetchUrl = async () => {
      try {
        const res = await apiRequest("POST", "/api/nm-files/signed-url", { gcsUrl });
        const data = await res.json();
        setSignedUrl(data.url);
      } catch {
        setError(true);
      } finally {
        setIsLoading(false);
      }
    };
    fetchUrl();
  }, [photo.url, photo.filename]);

  return (
    <div
      className={cn(
        "kii-photo-grid-item group",
        selected && "ring-2 ring-primary",
        onClick && "cursor-pointer",
        className,
      )}
      onClick={onClick}
    >
      <div className={preserveAspectRatio ? "w-full rounded-[calc(var(--radius)-2px)] overflow-hidden bg-muted" : "kii-photo-grid-thumbnail"}>
        {isLoading ? (
          <Skeleton className={preserveAspectRatio ? "w-full h-24" : "w-full h-full"} />
        ) : error || !signedUrl ? (
          <div className={cn("w-full flex items-center justify-center bg-muted", preserveAspectRatio ? "h-24" : "h-full")}>
            <Icon name={iconNames.image} size={24} className="text-muted-foreground" />
          </div>
        ) : (
          <img
            src={signedUrl}
            alt={photo.filename || "Photo"}
            className={preserveAspectRatio ? "w-full h-auto object-contain" : "w-full h-full object-cover"}
          />
        )}
      </div>
      {actions && (
        <div className="kii-photo-grid-actions">
          {actions}
        </div>
      )}
    </div>
  );
}
